import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FILE_ACCEPT, MAX_UPLOAD_BYTES, ONBOARDING_TAG, videoEmbed } from '@vtf/shared';
import { api, apiUpload, errorText } from '../lib/api';
import { fmtBytes, LEVEL_LABEL, LEVELS, toAudience, type Audience, type Collection, type FileInfo, type LibraryItem, type StrategyDetail } from '../lib/library';
import { Button, Card, cx, ErrorText, Field, Input, PageHeader, Select, Textarea } from '../components/ui';
import { AudiencePicker } from '../components/library/AudiencePicker';

/** Master Trader / Admin editors for library items (INT-LIB-01/02/04) and strategies (INT-LIB-03). */

interface Organise {
  collectionId: string;
  sortOrder: string;
  level: string;
  tagsText: string;
  onboarding: boolean;
  audience: Audience;
}

const parseTags = (o: Organise) => {
  const tags = o.tagsText.split(',').map((t) => t.trim().toLowerCase()).filter((t) => t && t !== ONBOARDING_TAG);
  return [...new Set([...tags, ...(o.onboarding ? [ONBOARDING_TAG] : [])])];
};
const organiseFrom = (i: LibraryItem): Organise => ({ collectionId: i.collection?.id ?? '', sortOrder: String(i.sortOrder), level: i.level ?? '', tagsText: i.tags.filter((t) => t !== ONBOARDING_TAG).join(', '), onboarding: i.onboarding, audience: toAudience(i.audience) });
const blankOrganise = (): Organise => ({ collectionId: '', sortOrder: '0', level: '', tagsText: '', onboarding: false, audience: { floor: true, marketIds: [], userIds: [] } });
const organiseBody = (o: Organise) => ({ collectionId: o.collectionId || null, sortOrder: Number(o.sortOrder) || 0, level: o.level || null, tags: parseTags(o), audience: o.audience });

function OrganiseCard({ value, onChange }: { value: Organise; onChange: (o: Organise) => void }) {
  const collections = useQuery({ queryKey: ['library-collections'], queryFn: () => api<Collection[]>('/library/collections') });
  return (
    <Card className="flex flex-col gap-3.5 p-4">
      <h2 className="m-0 text-[15px] font-semibold">Organise and share</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Collection or course">
          <Select value={value.collectionId} onChange={(e) => onChange({ ...value, collectionId: e.target.value })}>
            <option value="">None</option>
            {collections.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Order in collection" hint="lower comes first">
          <Input inputMode="numeric" value={value.sortOrder} onChange={(e) => onChange({ ...value, sortOrder: e.target.value })} />
        </Field>
        <Field label="Level">
          <Select value={value.level} onChange={(e) => onChange({ ...value, level: e.target.value })}>
            <option value="">Any level</option>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {LEVEL_LABEL[l]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Tags" hint="separate with commas" className="sm:col-span-3">
          <Input value={value.tagsText} onChange={(e) => onChange({ ...value, tagsText: e.target.value })} placeholder="psx, opening, order flow" />
        </Field>
      </div>
      <label className="flex min-h-10 items-center gap-2">
        <input type="checkbox" className="h-[18px] w-[18px]" checked={value.onboarding} onChange={(e) => onChange({ ...value, onboarding: e.target.checked })} />
        Onboarding: give this to every new trader to read when they join
      </label>
      <AudiencePicker value={value.audience} onChange={(audience) => onChange({ ...value, audience })} />
    </Card>
  );
}

function useUpload() {
  return useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_UPLOAD_BYTES) throw new Error(`That file is ${fmtBytes(file.size)}. The limit is 25 MB.`);
      const form = new FormData();
      form.append('file', file, file.name);
      return apiUpload<FileInfo>('/files', form);
    },
  });
}

interface ItemForm {
  type: 'VIDEO_LINK' | 'DOCUMENT';
  title: string;
  description: string;
  url: string;
  file: FileInfo | null;
  allowDownload: boolean;
  watermark: boolean;
  organise: Organise;
}

export function LibraryItemEditor() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const fileInput = useId();
  const existing = useQuery({ queryKey: ['library-item', id], queryFn: () => api<LibraryItem>(`/library/items/${id}`), enabled: !!id });
  const [f, setF] = useState<ItemForm | null>(() => (id ? null : { type: search.get('type') === 'DOCUMENT' ? 'DOCUMENT' : 'VIDEO_LINK', title: '', description: '', url: '', file: null, allowDownload: true, watermark: true, organise: blankOrganise() }));
  useEffect(() => {
    const i = existing.data;
    if (i && !f && (i.type === 'VIDEO_LINK' || i.type === 'DOCUMENT')) setF({ type: i.type, title: i.title, description: i.description ?? '', url: i.url ?? '', file: i.file, allowDownload: i.allowDownload, watermark: i.watermark, organise: organiseFrom(i) });
  }, [existing.data, f]);
  const upload = useUpload();
  const save = useMutation({
    mutationFn: async (publish: boolean) => {
      if (!f) throw new Error('Nothing to save');
      const common = { title: f.title, description: f.description || null, ...organiseBody(f.organise) };
      const typeFields = f.type === 'VIDEO_LINK' ? { url: f.url } : { fileId: f.file?.id, allowDownload: f.allowDownload, watermark: f.watermark };
      if (id) return api<LibraryItem>(`/library/items/${id}`, { method: 'PATCH', body: { ...common, ...typeFields } });
      return api<LibraryItem>('/library/items', { body: { type: f.type, ...common, ...typeFields, publish } });
    },
    onSuccess: (i) => {
      void qc.invalidateQueries({ queryKey: ['library'] });
      void qc.invalidateQueries({ queryKey: ['library-item', i.id] });
      nav(`/library/items/${i.id}`);
    },
  });
  if (existing.error) return <ErrorText error={errorText(existing.error)} />;
  if (!f) return null;
  const embed = f.type === 'VIDEO_LINK' && f.url ? videoEmbed(f.url) : null;
  const ready = f.title.trim().length >= 2 && (f.type === 'VIDEO_LINK' ? /^https?:\/\//.test(f.url) : !!f.file);

  return (
    <>
      <PageHeader
        title={id ? `Edit: ${existing.data?.title ?? ''}` : f.type === 'VIDEO_LINK' ? 'New video' : 'New document'}
        back={<Link to={id ? `/library/items/${id}` : '/library'} className="text-[13px]">← {id ? 'Back to the item' : 'Library'}</Link>}
        right={
          id ? (
            <Button variant="primary" onClick={() => save.mutate(false)} disabled={!ready || save.isPending}>
              Save changes
            </Button>
          ) : (
            <>
              <Button onClick={() => save.mutate(false)} disabled={!ready || save.isPending}>
                Save draft
              </Button>
              <Button variant="primary" onClick={() => save.mutate(true)} disabled={!ready || save.isPending}>
                Publish
              </Button>
            </>
          )
        }
      />
      <ErrorText error={save.error ? errorText(save.error) : null} />
      <div className="flex max-w-[900px] flex-col gap-3.5">
        <Card className="flex flex-col gap-3.5 p-4">
          <Field label="Title">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={f.type === 'VIDEO_LINK' ? 'e.g. Reading the PSX opening drive' : 'e.g. Floor risk rules'} />
          </Field>
          {f.type === 'VIDEO_LINK' ? (
            <Field label="Video link" hint={embed ? (embed.provider ? `Plays here in the ${embed.provider === 'youtube' ? 'privacy-enhanced YouTube' : 'Vimeo'} player.` : 'This host can’t be embedded, so traders get a link to open it.') : 'YouTube, Vimeo, Google Drive or any other link'}>
              <Input type="url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" />
            </Field>
          ) : (
            <div className="flex flex-col gap-2">
              <label htmlFor={fileInput} className="text-xs text-muted">
                File · PDF, Word, Excel, PowerPoint, images or text · up to 25 MB
              </label>
              <input
                id={fileInput}
                type="file"
                accept={FILE_ACCEPT}
                className="block w-full cursor-pointer rounded-lg border border-line2 bg-field px-3 py-2.5 text-sub file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-[#1a1f27] file:px-3 file:py-1.5 file:font-semibold file:text-ink"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) upload.mutate(file, { onSuccess: (fi) => setF((prev) => (prev ? { ...prev, file: fi, title: prev.title || fi.filename.replace(/\.[^.]+$/, '') } : prev)) });
                }}
              />
              {upload.isPending && <span className="text-sm text-sub">Uploading…</span>}
              <ErrorText error={upload.error ? errorText(upload.error) : null} />
              {f.file && (
                <span className="text-sm text-up" role="status">
                  ✓ {f.file.filename} · {fmtBytes(f.file.sizeBytes)}
                </span>
              )}
              <label className="flex min-h-10 items-center gap-2">
                <input type="checkbox" className="h-[18px] w-[18px]" checked={f.allowDownload} onChange={(e) => setF({ ...f, allowDownload: e.target.checked })} />
                Allow traders to download it (PDFs and images can always be viewed)
              </label>
              <label className="flex min-h-10 items-center gap-2">
                <input type="checkbox" className="h-[18px] w-[18px]" checked={f.watermark} onChange={(e) => setF({ ...f, watermark: e.target.checked })} />
                Show the viewer's name over image previews
              </label>
            </div>
          )}
          <Field label="Description (optional)">
            <Textarea rows={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What traders will learn, and what to look out for" />
          </Field>
        </Card>
        <OrganiseCard value={f.organise} onChange={(organise) => setF({ ...f, organise })} />
      </div>
    </>
  );
}

// ───────────────────────── Strategy editor ─────────────────────────

interface ContentForm {
  markets: string[];
  timeframes: string;
  concept: string;
  setupConditions: string;
  entryTrigger: string;
  stopLossRule: string;
  takeProfitRule: string;
  riskRule: string;
  noTradeFilters: string;
  checklist: { id?: string; text: string; required: boolean }[];
  examples: { caption: string; url: string; file: FileInfo | null }[];
  relatedItemIds: string[];
}

const blankContent = (): ContentForm => ({ markets: [], timeframes: '', concept: '', setupConditions: '', entryTrigger: '', stopLossRule: '', takeProfitRule: '', riskRule: '', noTradeFilters: '', checklist: [{ text: '', required: false }], examples: [], relatedItemIds: [] });

function contentFrom(d: StrategyDetail): ContentForm {
  const v = d.version;
  return {
    markets: v.markets,
    timeframes: v.timeframes.join(', '),
    concept: v.concept,
    setupConditions: v.setupConditions,
    entryTrigger: v.entryTrigger,
    stopLossRule: v.stopLossRule,
    takeProfitRule: v.takeProfitRule,
    riskRule: v.riskRule,
    noTradeFilters: v.noTradeFilters ?? '',
    checklist: v.checklist.map((c) => ({ id: c.id, text: c.text, required: c.required })),
    examples: v.examples.map((e) => ({ caption: e.caption, url: e.url ?? '', file: e.file })),
    relatedItemIds: v.relatedItemIds,
  };
}

function contentBody(c: ContentForm, changelog: string) {
  return {
    markets: c.markets,
    timeframes: c.timeframes.split(',').map((t) => t.trim()).filter(Boolean),
    concept: c.concept,
    setupConditions: c.setupConditions,
    entryTrigger: c.entryTrigger,
    stopLossRule: c.stopLossRule,
    takeProfitRule: c.takeProfitRule,
    riskRule: c.riskRule,
    noTradeFilters: c.noTradeFilters || null,
    checklist: c.checklist.filter((x) => x.text.trim()).map((x) => ({ ...(x.id ? { id: x.id } : {}), text: x.text, required: x.required })),
    examples: c.examples.filter((e) => e.file || e.url.trim()).map((e) => (e.file ? { caption: e.caption, fileId: e.file.id } : { caption: e.caption, url: e.url.trim() })),
    relatedItemIds: c.relatedItemIds,
    changelog: changelog || null,
  };
}

function TextBlock({ label, value, onChange, hint, rows = 3 }: { label: string; value: string; onChange: (v: string) => void; hint?: ReactNode; rows?: number }) {
  return (
    <Field label={label} hint={hint}>
      <Textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

export function StrategyEditor() {
  const { code } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const existing = useQuery({ queryKey: ['strategy', code, null], queryFn: () => api<StrategyDetail>(`/library/strategies/${encodeURIComponent(code!)}`), enabled: !!code });
  const markets = useQuery({ queryKey: ['markets'], queryFn: () => api<{ id: string; code: string }[]>('/markets'), staleTime: 300_000 });
  const library = useQuery({ queryKey: ['library', 'for-strategy'], queryFn: () => api<LibraryItem[]>('/library') });
  const [meta, setMeta] = useState<{ code: string; name: string; description: string; organise: Organise } | null>(() => (code ? null : { code: '', name: '', description: '', organise: blankOrganise() }));
  const [content, setContent] = useState<ContentForm | null>(() => (code ? null : blankContent()));
  const [changelog, setChangelog] = useState('');
  useEffect(() => {
    const d = existing.data;
    if (d && !meta) {
      setMeta({ code: d.strategy.code, name: d.strategy.name, description: d.item.description ?? '', organise: organiseFrom(d.item) });
      setContent(contentFrom(d));
    }
  }, [existing.data, meta]);
  const upload = useUpload();
  const original = useMemo(() => (existing.data ? JSON.stringify(contentBody(contentFrom(existing.data), '')) : null), [existing.data]);
  const published = !!existing.data?.item.publishedAt;
  const contentChanged = !!content && original !== JSON.stringify(contentBody(content, ''));

  const save = useMutation({
    mutationFn: async (publish: boolean) => {
      if (!meta || !content) throw new Error('Nothing to save');
      if (!code) {
        const d = await api<StrategyDetail>('/library/strategies', { body: { code: meta.code, name: meta.name, description: meta.description || null, ...organiseBody(meta.organise), content: contentBody(content, ''), publish } });
        return d.strategy.code;
      }
      const d = existing.data!;
      await api(`/library/items/${d.item.id}`, { method: 'PATCH', body: { title: meta.name, description: meta.description || null, ...organiseBody(meta.organise) } });
      if (contentChanged) await api(`/library/strategies/${d.strategy.id}/versions`, { body: contentBody(content, changelog) });
      return d.strategy.code;
    },
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['library'] });
      void qc.invalidateQueries({ queryKey: ['strategy'] });
      nav(`/library/strategies/${encodeURIComponent(c)}`);
    },
  });

  if (existing.error) return <ErrorText error={errorText(existing.error)} />;
  if (!meta || !content) return null;
  const set = (patch: Partial<ContentForm>) => setContent({ ...content, ...patch });
  const setItem = (n: number, patch: Partial<ContentForm['checklist'][number]>) => set({ checklist: content.checklist.map((c, i) => (i === n ? { ...c, ...patch } : c)) });
  const move = (n: number, by: number) => {
    const list = [...content.checklist];
    const [x] = list.splice(n, 1);
    list.splice(n + by, 0, x!);
    set({ checklist: list });
  };
  const nextVersion = (existing.data?.strategy.currentVersion ?? 0) + 1;
  const needsLog = !!code && published && contentChanged;
  const ready = meta.code.trim().length >= 2 && meta.name.trim().length >= 2 && content.checklist.some((c) => c.text.trim()) && (!needsLog || changelog.trim().length > 0);
  const others = (library.data ?? []).filter((i) => i.type !== 'STRATEGY' && i.status !== 'ARCHIVED');

  return (
    <>
      <PageHeader
        title={code ? `${meta.code} · ${published ? `new version (v${nextVersion})` : 'edit draft'}` : 'New strategy'}
        sub={code && published ? 'Publishing a change creates a new version. Earlier versions stay exactly as they were.' : undefined}
        back={<Link to={code ? `/library/strategies/${encodeURIComponent(code)}` : '/library'} className="text-[13px]">← {code ? 'Back to the strategy' : 'Library'}</Link>}
        right={
          code ? (
            <Button variant="primary" onClick={() => save.mutate(false)} disabled={!ready || save.isPending}>
              {published && contentChanged ? `Publish v${nextVersion}` : 'Save changes'}
            </Button>
          ) : (
            <>
              <Button onClick={() => save.mutate(false)} disabled={!ready || save.isPending}>
                Save draft
              </Button>
              <Button variant="primary" onClick={() => save.mutate(true)} disabled={!ready || save.isPending}>
                Publish v1
              </Button>
            </>
          )
        }
      />
      <ErrorText error={save.error ? errorText(save.error) : null} />
      <div className="flex max-w-[960px] flex-col gap-3.5">
        <Card className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-4">
          <Field label="Code" hint="2–12 letters, e.g. VMS">
            <Input value={meta.code} disabled={!!code} onChange={(e) => setMeta({ ...meta, code: e.target.value.toUpperCase() })} className="font-mono" />
          </Field>
          <Field label="Name" className="sm:col-span-3">
            <Input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} placeholder="e.g. Volume shelf retest" />
          </Field>
          <Field label="One-line summary (optional)" className="sm:col-span-4">
            <Input value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} />
          </Field>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-xs text-muted">Markets</span>
            <div role="group" aria-label="Markets" className="flex flex-wrap gap-1.5">
              {markets.data?.map((m) => {
                const on = content.markets.includes(m.code);
                return (
                  <button key={m.id} type="button" aria-pressed={on} onClick={() => set({ markets: on ? content.markets.filter((x) => x !== m.code) : [...content.markets, m.code] })} className={cx('min-h-9 cursor-pointer rounded-full px-3.5 py-1.5 text-sm', on ? 'border-0 bg-accent font-semibold text-bg' : 'border border-line2 bg-panel text-sub')}>
                    {m.code}
                  </button>
                );
              })}
            </div>
          </div>
          <Field label="Timeframes" hint="separate with commas" className="sm:col-span-2">
            <Input value={content.timeframes} onChange={(e) => set({ timeframes: e.target.value })} placeholder="H1, M15" />
          </Field>
        </Card>

        <Card className="flex flex-col gap-3.5 p-4">
          <h2 className="m-0 text-[15px] font-semibold">Rules</h2>
          <TextBlock label="Concept" value={content.concept} onChange={(v) => set({ concept: v })} rows={4} />
          <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
            <TextBlock label="Setup conditions" value={content.setupConditions} onChange={(v) => set({ setupConditions: v })} />
            <TextBlock label="Entry trigger" value={content.entryTrigger} onChange={(v) => set({ entryTrigger: v })} />
            <TextBlock label="Stop-loss rule" value={content.stopLossRule} onChange={(v) => set({ stopLossRule: v })} />
            <TextBlock label="Take-profit rule" value={content.takeProfitRule} onChange={(v) => set({ takeProfitRule: v })} />
            <TextBlock label="Risk rule" value={content.riskRule} onChange={(v) => set({ riskRule: v })} />
            <TextBlock label="Don't trade when… (optional)" value={content.noTradeFilters} onChange={(v) => set({ noTradeFilters: v })} />
          </div>
        </Card>

        <Card className="flex flex-col gap-3 p-4">
          <div className="flex flex-col gap-0.5">
            <h2 className="m-0 text-[15px] font-semibold">Pre-trade checklist</h2>
            <span className="text-sm text-sub">Traders tick these on their trade plan. Must-haves stand out to the Risk Manager when left unticked.</span>
          </div>
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {content.checklist.map((c, n) => (
              <li key={c.id ?? `new-${n}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-raised p-2">
                <span className="w-5 text-center font-mono text-xs text-muted">{n + 1}</span>
                <Input aria-label={`Checklist item ${n + 1}`} value={c.text} onChange={(e) => setItem(n, { text: e.target.value })} className="min-w-0 flex-[1_1_220px]" placeholder="e.g. Higher-timeframe trend agrees" />
                <label className="flex min-h-10 items-center gap-1.5 text-sm">
                  <input type="checkbox" className="h-[18px] w-[18px]" checked={c.required} onChange={(e) => setItem(n, { required: e.target.checked })} /> Must-have
                </label>
                <span className="flex gap-1">
                  <Button variant="ghost" className="min-h-9 px-2.5" aria-label={`Move item ${n + 1} up`} disabled={n === 0} onClick={() => move(n, -1)}>
                    ↑
                  </Button>
                  <Button variant="ghost" className="min-h-9 px-2.5" aria-label={`Move item ${n + 1} down`} disabled={n === content.checklist.length - 1} onClick={() => move(n, 1)}>
                    ↓
                  </Button>
                  <Button variant="ghost" className="min-h-9 px-2.5" aria-label={`Remove item ${n + 1}`} disabled={content.checklist.length === 1} onClick={() => set({ checklist: content.checklist.filter((_, i) => i !== n) })}>
                    ✕
                  </Button>
                </span>
              </li>
            ))}
          </ol>
          <Button className="self-start" onClick={() => set({ checklist: [...content.checklist, { text: '', required: false }] })} disabled={content.checklist.length >= 30}>
            + Add checklist item
          </Button>
        </Card>

        <Card className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-[15px] font-semibold">Example charts</h2>
          {content.examples.map((e, n) => (
            <div key={n} className="flex flex-wrap items-end gap-2 rounded-lg bg-raised p-2">
              {e.file ? (
                <span className="min-w-0 flex-[1_1_200px] truncate py-2 text-sm">🖼 {e.file.filename}</span>
              ) : (
                <Field label="Chart link" className="min-w-0 flex-[1_1_220px]">
                  <Input type="url" value={e.url} onChange={(ev) => set({ examples: content.examples.map((x, i) => (i === n ? { ...x, url: ev.target.value } : x)) })} placeholder="https://www.tradingview.com/x/…" />
                </Field>
              )}
              <Field label="Caption" className="min-w-0 flex-[1_1_200px]">
                <Input value={e.caption} onChange={(ev) => set({ examples: content.examples.map((x, i) => (i === n ? { ...x, caption: ev.target.value } : x)) })} />
              </Field>
              <Button variant="ghost" aria-label={`Remove example ${n + 1}`} onClick={() => set({ examples: content.examples.filter((_, i) => i !== n) })}>
                ✕
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => set({ examples: [...content.examples, { caption: '', url: '', file: null }] })} disabled={content.examples.length >= 12}>
              + Add a chart link
            </Button>
            <label className="min-h-10 cursor-pointer rounded-lg border border-[#2b3340] bg-[#1a1f27] px-4 py-2 text-sm font-semibold">
              + Upload a chart image
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.webp"
                className="sr-only"
                onChange={(ev) => {
                  const file = ev.target.files?.[0];
                  if (file) upload.mutate(file, { onSuccess: (fi) => setContent((prev) => (prev ? { ...prev, examples: [...prev.examples, { caption: '', url: '', file: fi }] } : prev)) });
                  ev.target.value = '';
                }}
              />
            </label>
            {upload.isPending && <span className="text-sm text-sub">Uploading…</span>}
          </div>
          <ErrorText error={upload.error ? errorText(upload.error) : null} />
        </Card>

        {others.length > 0 && (
          <Card className="flex flex-col gap-2 p-4">
            <h2 className="m-0 text-[15px] font-semibold">Linked videos and documents</h2>
            <div className="flex max-h-56 flex-col overflow-y-auto">
              {others.map((i) => (
                <label key={i.id} className="flex min-h-9 items-center gap-2">
                  <input type="checkbox" className="h-[18px] w-[18px]" checked={content.relatedItemIds.includes(i.id)} onChange={() => set({ relatedItemIds: content.relatedItemIds.includes(i.id) ? content.relatedItemIds.filter((x) => x !== i.id) : [...content.relatedItemIds, i.id] })} />
                  <span>{i.title}</span>
                  <span className="text-xs text-muted">{i.type === 'VIDEO_LINK' ? 'video' : 'document'}</span>
                </label>
              ))}
            </div>
          </Card>
        )}

        <OrganiseCard value={meta.organise} onChange={(organise) => setMeta({ ...meta, organise })} />

        {code && published && (
          <Card tone={needsLog && !changelog.trim() ? 'warn' : undefined} className="p-4">
            <Field label={`What changed in v${nextVersion}?`} hint={contentChanged ? 'Required. Traders and followers see this in the version history.' : 'Only needed when you change the rules or the checklist.'}>
              <Textarea rows={2} value={changelog} onChange={(e) => setChangelog(e.target.value)} placeholder="e.g. Tightened the stop to below the shelf low" />
            </Field>
          </Card>
        )}
      </div>
    </>
  );
}
